import React, { useState, useEffect, useMemo, useCallback } from "react";
import * as env from "shared/env";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { useSelector } from "react-redux";
import { useAppThunkDispatch, RootState } from "redux/store";
import { styles } from "./style";
import { Lead } from "model/lead";
import { getMarketsOptimized, getUnsubscribedReportsByLeadId } from "redux/selector";
import { createMarketReportSubscription } from "redux/slice/market-report-subscriptions";
import { FullPageModal } from "component/shared/full-page-modal";
import Autocomplete from "@mui/material/Autocomplete";

interface Props extends WithStyles<typeof styles> {
	open: boolean;
	onClose: Function;
	lead: Lead;
}

const Component: React.FC<Props> = ({ classes = {}, open, onClose, lead }) => {
	// Redux hooks
	const dispatch = useAppThunkDispatch();
	const markets = useSelector(getMarketsOptimized);
	const unsubscribedReports = useSelector((state: RootState) => 
		getUnsubscribedReportsByLeadId(state, lead.id)
	);

	// Local state
	const [selectedMarketId, setSelectedMarketId] = useState<number | undefined>(undefined);
	const [listingChecked, setListingChecked] = useState<boolean>(true);
	const [openHomesChecked, setOpenHomesChecked] = useState<boolean>(true);
	const [marketChecked, setMarketChecked] = useState<boolean>(true);

	// Reset state when dialog opens
	useEffect(() => {
		if (open) {
			setSelectedMarketId(undefined);
			setListingChecked(true);
			setOpenHomesChecked(true);
			setMarketChecked(true);
		}
	}, [open]);

	// Memoized selected market
	const selectedMarket = useMemo(() => {
		return markets.find(market => market.id === selectedMarketId);
	}, [markets, selectedMarketId]);

	// Memoized sorted markets for autocomplete
	const marketsSorted = useMemo(() => {
		return markets
			.sort((a, b) => a.name.localeCompare(b.name))
			.map(elem => ({
				name: elem.name,
				id: elem.id
			}));
	}, [markets]);

	// Memoized isSaveable calculation
	const isSaveable = useMemo(() => {
		if (!selectedMarketId) {
			return true;
		} else if (!listingChecked && !openHomesChecked && !marketChecked) {
			return true;
		} else if (selectedMarket) {
			if (
				(listingChecked && selectedMarket.listingReport) || 
				(openHomesChecked && selectedMarket.openHomesReport) ||
				(marketChecked && selectedMarket.marketReport)) {
					return false;
				}
		} 
		return true;
	}, [selectedMarketId, listingChecked, openHomesChecked, marketChecked, selectedMarket]);

	// Create subscription function
	const createSubscription = useCallback(() => {
		if (selectedMarket && unsubscribedReports) {
			const unsubscribedIds = unsubscribedReports.map((report) => report.id);
			
			if (listingChecked && selectedMarket.listingReport && unsubscribedIds.includes(selectedMarket.listingReport.id)) {
				dispatch(createMarketReportSubscription({ 
					subscription: {
						lead,
						report: selectedMarket.listingReport,
						inactive: false,
					}
				}));
			}
			
			if (openHomesChecked && selectedMarket.openHomesReport && unsubscribedIds.includes(selectedMarket.openHomesReport.id)) {
				dispatch(createMarketReportSubscription({ 
					subscription: {
						lead,
						report: selectedMarket.openHomesReport,
						inactive: false,
					}
				}));
			}
			
			if (marketChecked && selectedMarket.marketReport && unsubscribedIds.includes(selectedMarket.marketReport.id)) {
				dispatch(createMarketReportSubscription({ 
					subscription: {
						lead,
						report: selectedMarket.marketReport,
						inactive: false,
					}
				}));
			}
		}
	}, [selectedMarket, unsubscribedReports, listingChecked, openHomesChecked, marketChecked, lead, dispatch]);

	// Event handlers
	const handleMarketChange = useCallback((event: any, marketOption: any) => {
		setSelectedMarketId(marketOption?.id);
	}, []);

	const handleListingCheckedChange = useCallback(() => {
		setListingChecked(!listingChecked);
	}, [listingChecked]);

	const handleOpenHomesCheckedChange = useCallback(() => {
		setOpenHomesChecked(!openHomesChecked);
	}, [openHomesChecked]);

	const handleMarketCheckedChange = useCallback(() => {
		setMarketChecked(!marketChecked);
	}, [marketChecked]);

	const handleSave = useCallback(() => {
		onClose();
		createSubscription();
	}, [onClose, createSubscription]);

	const handleCancel = useCallback(() => {
		onClose();
	}, [onClose]);

	const actions = (
		<>
			<Mui.Button onClick={handleCancel} variant="outlined" color="primary">
				Cancel
			</Mui.Button>
			<Mui.Button onClick={handleSave} variant="contained" color="primary" autoFocus disabled={isSaveable}>
				Add Market Subscription
			</Mui.Button>
		</>
	);

	return (
		<FullPageModal open={open} onClose={handleCancel} title="Add Market Subscription" actions={actions}>
			<Mui.FormControl className={classes.formControl}>
				<Mui.Typography>Market</Mui.Typography>
				<Autocomplete
					fullWidth
					options={marketsSorted}
					getOptionLabel={(option: any) => option.name}
					renderOption={(props, option) => (
						<li {...props}>
							{option.name}
						</li>
					)}
					renderInput={(params) => <Mui.TextField {...params} placeholder="Select market" variant="outlined" size="small" />}
					onChange={handleMarketChange}
				/>
				{selectedMarket &&
					<div>
						{selectedMarket.listingReport &&
							<Mui.FormControlLabel
								control={
									<Mui.Checkbox
										checked={listingChecked}
										onChange={handleListingCheckedChange}
										color="primary"
									/>
								}
								label={selectedMarket.listingReport.type.label}
							/>
						}
						{selectedMarket.openHomesReport &&
							<Mui.FormControlLabel
								control={
									<Mui.Checkbox
										checked={openHomesChecked}
										onChange={handleOpenHomesCheckedChange}
										color="primary"
									/>
								}
								label={selectedMarket.openHomesReport.type.label}
							/>
						}
						{selectedMarket.marketReport &&
							<Mui.FormControlLabel
								control={
									<Mui.Checkbox
										checked={marketChecked}
										onChange={handleMarketCheckedChange}
										color="primary"
									/>
								}
								label={selectedMarket.marketReport.type.label}
							/>
						}
					</div>
				}
			</Mui.FormControl>
		</FullPageModal>
	);
};

export const MarketDialog = withStyles(Component, styles);